import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  SlidersHorizontal,
  X,
  Music2,
  Tag,
  User,
  Hash,
  Globe,
  Disc3,
  Zap,
  Filter,
} from 'lucide-react';
import api from '../services/api';

const EMPTY_FILTERS = {
  query: '',
  artist: '',
  genre: '',
  status: '',
  key_signature: '',
  bpm_min: '',
  bpm_max: '',
  isrc: '',
  isni: '',
  tags: '',
  album: '',
};

const STATUS_OPTIONS = ['pending', 'approved', 'rejected'];

const DEFAULT_KEY_OPTIONS = [
  'C maj', 'C min', 'C# maj', 'C# min', 'D maj', 'D min', 'D# maj', 'D# min',
  'E maj', 'E min', 'F maj', 'F min', 'F# maj', 'F# min', 'G maj', 'G min',
  'G# maj', 'G# min', 'A maj', 'A min', 'A# maj', 'A# min', 'B maj', 'B min',
];

function resultHref(result, isAdmin) {
  switch (result.type) {
    case 'master_track':
      return isAdmin ? '/master-tracks' : '/submissions';
    case 'artist':
      return `/artist/${result.id}`;
    case 'submission':
      return '/submissions';
    case 'artist_submission':
      return '/submissions';
    default:
      return '/dashboard';
  }
}

function resultTypeLabel(type) {
  switch (type) {
    case 'master_track':
      return 'Catalog Track';
    case 'artist':
      return 'Artist';
    case 'submission':
      return 'Track Submission';
    case 'artist_submission':
      return 'Artist Submission';
    default:
      return 'Result';
  }
}

function FilterField({
  label,
  icon,
  value,
  onChange,
  onFocus,
  onBlur,
  onKeyDown,
  placeholder,
  suggestions,
  suggestionIndex,
  onSelect,
}) {
  return (
    <div className="space-y-1 relative">
      <label className="flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider text-gray-500">
        {icon} {label}
      </label>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={onFocus}
        onBlur={onBlur}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        className="w-full bg-white/[0.03] border border-white/[0.08] rounded px-2.5 py-1.5 text-xs text-white outline-none focus:border-[#C4AB04]/40 transition placeholder-gray-600"
      />
      {suggestions.length > 0 && (
        <div className="absolute top-full left-0 right-0 mt-0.5 z-30 bg-[#111] border border-white/10 rounded-md shadow-xl max-h-[160px] overflow-y-auto">
          {suggestions.map((s, i) => (
            <button
              key={s}
              type="button"
              onMouseDown={(e) => {
                e.preventDefault();
                onSelect(s);
              }}
              className={`w-full text-left px-3 py-1.5 text-xs transition-colors ${
                i === suggestionIndex
                  ? 'bg-[#C4AB04]/10 text-[#C4AB04]'
                  : 'text-gray-300 hover:bg-white/[0.04] hover:text-white'
              }`}
            >
              {s}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function StatusPill({ status }) {
  const colors = {
    pending: 'text-[#C4AB04] bg-[#C4AB04]/10',
    approved: 'text-[#39FF14] bg-[#39FF14]/10',
    rejected: 'text-[#FF003C] bg-[#FF003C]/10',
    draft: 'text-gray-400 bg-gray-400/10',
    in_review: 'text-[#C4AB04] bg-[#C4AB04]/10',
    published: 'text-[#00FFFF] bg-[#00FFFF]/10',
  };
  const cls = colors[status] || colors.pending;
  return (
    <span className={`px-1.5 py-0.5 rounded text-[8px] font-bold uppercase tracking-wider ${cls}`}>
      {(status || 'unknown').replace('_', ' ')}
    </span>
  );
}

const CatalogSearch = ({ isAdmin = false }) => {
  const navigate = useNavigate();
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [advanced, setAdvanced] = useState(false);
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [focused, setFocused] = useState(false);
  const inputRef = useRef(null);
  const containerRef = useRef(null);

  const [suggestions, setSuggestions] = useState({
    artist: [],
    genre: [],
    album: [],
    tags: [],
    isrc: [],
    key_signature: DEFAULT_KEY_OPTIONS,
  });
  const [activeSuggestionField, setActiveSuggestionField] = useState(null);
  const [filteredSuggestions, setFilteredSuggestions] = useState([]);
  const [suggestionIndex, setSuggestionIndex] = useState(-1);

  useEffect(() => {
    let cancelled = false;

    async function loadSuggestions() {
      try {
        const response = await api.get('/api/users/catalog-search/suggestions');
        if (cancelled) return;
        const data = response.data.suggestions || {};
        setSuggestions((prev) => ({
          ...prev,
          artist: data.artist || [],
          album: data.album || [],
          genre: data.genre || [],
          tags: data.tags || [],
          isrc: data.isrc || [],
          key_signature: data.key_signature?.length ? data.key_signature : DEFAULT_KEY_OPTIONS,
        }));
      } catch (error) {
        console.error('Failed to load catalog search suggestions:', error);
      }
    }

    loadSuggestions();
    return () => {
      cancelled = true;
    };
  }, []);

  const updateSuggestions = useCallback(
    (field, value) => {
      const pool = suggestions[field] || [];
      if (!value.trim()) {
        setFilteredSuggestions(pool.slice(0, 8));
      } else {
        const lower = value.toLowerCase();
        setFilteredSuggestions(pool.filter((s) => s.toLowerCase().includes(lower)).slice(0, 8));
      }
      setSuggestionIndex(-1);
    },
    [suggestions],
  );

  const handleFieldFocus = (field) => {
    setActiveSuggestionField(field);
    updateSuggestions(field, filters[field] ?? '');
  };

  const handleFieldBlur = () => {
    setTimeout(() => setActiveSuggestionField(null), 150);
  };

  const selectSuggestion = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
    setActiveSuggestionField(null);
  };

  const handleFieldKeyDown = (e, field) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSuggestionIndex((i) => Math.min(i + 1, filteredSuggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSuggestionIndex((i) => Math.max(i - 1, -1));
    } else if (e.key === 'Enter' && suggestionIndex >= 0) {
      e.preventDefault();
      selectSuggestion(field, filteredSuggestions[suggestionIndex]);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      executeSearch();
    } else if (e.key === 'Escape') {
      setActiveSuggestionField(null);
    }
  };

  const executeSearch = async () => {
    setSearching(true);
    setHasSearched(true);

    try {
      const response = await api.get('/api/users/catalog-search', { params: filters });
      setResults(response.data.results || []);
    } catch (error) {
      console.error('Catalog search failed:', error);
      setResults([]);
    } finally {
      setSearching(false);
    }
  };

  const clearSearch = () => {
    setFilters(EMPTY_FILTERS);
    setResults([]);
    setHasSearched(false);
  };

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.artist) count += 1;
    if (filters.genre) count += 1;
    if (filters.status) count += 1;
    if (filters.key_signature) count += 1;
    if (filters.bpm_min || filters.bpm_max) count += 1;
    if (filters.isrc) count += 1;
    if (filters.isni) count += 1;
    if (filters.tags) count += 1;
    if (filters.album) count += 1;
    return count;
  }, [filters]);

  const openResult = (result) => {
    navigate(resultHref(result, isAdmin));
  };

  return (
    <div className="relative" ref={containerRef}>
      <div
        className={`bg-black/90 rounded-lg shadow-[0_4px_4px_rgba(0,0,0,0.17)] border transition-all duration-300 ${
          focused ? 'border-[#C4AB04]/40 shadow-[0_0_20px_rgba(196,171,4,0.08)]' : 'border-white/[0.06]'
        }`}
      >
        <div className="flex items-center gap-3 px-4 py-3">
          <button
            type="button"
            onClick={executeSearch}
            className="text-gray-400 hover:text-[#C4AB04] transition-colors shrink-0"
            title="Search (Enter)"
          >
            <Search size={18} className={searching ? 'animate-pulse' : ''} />
          </button>

          <input
            ref={inputRef}
            type="text"
            value={filters.query}
            onChange={(e) => setFilters((prev) => ({ ...prev, query: e.target.value }))}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                executeSearch();
              }
            }}
            placeholder="Search tracks, artists, submissions, ISRC, writers..."
            className="flex-1 bg-transparent text-sm text-white placeholder-gray-500 outline-none"
          />

          {(filters.query || hasSearched) && (
            <button
              type="button"
              onClick={clearSearch}
              className="text-gray-500 hover:text-white transition-colors shrink-0"
            >
              <X size={14} />
            </button>
          )}

          <div className="w-px h-5 bg-white/10" />

          <button
            type="button"
            onClick={() => setAdvanced(!advanced)}
            className={`flex items-center gap-1.5 px-2.5 py-1 rounded text-[10px] font-bold uppercase tracking-wider transition-all ${
              advanced
                ? 'bg-[#C4AB04]/10 text-[#C4AB04] border border-[#C4AB04]/30'
                : 'text-gray-400 hover:text-white hover:bg-white/5'
            }`}
          >
            <SlidersHorizontal size={12} />
            Advanced
            {activeFilterCount > 0 && (
              <span className="ml-1 w-4 h-4 rounded-full bg-[#C4AB04]/20 text-[#C4AB04] text-[9px] flex items-center justify-center font-bold">
                {activeFilterCount}
              </span>
            )}
          </button>
        </div>

        {advanced && (
          <div className="px-4 pb-4 pt-1 border-t border-white/[0.04] animate-[slideDown_200ms_ease-out]">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 mt-3">
              <FilterField
                label="Artist"
                icon={<User size={10} />}
                value={filters.artist}
                onChange={(v) => {
                  setFilters((prev) => ({ ...prev, artist: v }));
                  updateSuggestions('artist', v);
                }}
                onFocus={() => handleFieldFocus('artist')}
                onBlur={handleFieldBlur}
                onKeyDown={(e) => handleFieldKeyDown(e, 'artist')}
                placeholder="Artist name..."
                suggestions={activeSuggestionField === 'artist' ? filteredSuggestions : []}
                suggestionIndex={suggestionIndex}
                onSelect={(v) => selectSuggestion('artist', v)}
              />
              <FilterField
                label="Album / Release"
                icon={<Disc3 size={10} />}
                value={filters.album}
                onChange={(v) => {
                  setFilters((prev) => ({ ...prev, album: v }));
                  updateSuggestions('album', v);
                }}
                onFocus={() => handleFieldFocus('album')}
                onBlur={handleFieldBlur}
                onKeyDown={(e) => handleFieldKeyDown(e, 'album')}
                placeholder="Release package..."
                suggestions={activeSuggestionField === 'album' ? filteredSuggestions : []}
                suggestionIndex={suggestionIndex}
                onSelect={(v) => selectSuggestion('album', v)}
              />
              <FilterField
                label="Genre"
                icon={<Music2 size={10} />}
                value={filters.genre}
                onChange={(v) => {
                  setFilters((prev) => ({ ...prev, genre: v }));
                  updateSuggestions('genre', v);
                }}
                onFocus={() => handleFieldFocus('genre')}
                onBlur={handleFieldBlur}
                onKeyDown={(e) => handleFieldKeyDown(e, 'genre')}
                placeholder="Genre..."
                suggestions={activeSuggestionField === 'genre' ? filteredSuggestions : []}
                suggestionIndex={suggestionIndex}
                onSelect={(v) => selectSuggestion('genre', v)}
              />
              <FilterField
                label="Tags"
                icon={<Tag size={10} />}
                value={filters.tags}
                onChange={(v) => {
                  setFilters((prev) => ({ ...prev, tags: v }));
                  updateSuggestions('tags', v);
                }}
                onFocus={() => handleFieldFocus('tags')}
                onBlur={handleFieldBlur}
                onKeyDown={(e) => handleFieldKeyDown(e, 'tags')}
                placeholder="Keywords..."
                suggestions={activeSuggestionField === 'tags' ? filteredSuggestions : []}
                suggestionIndex={suggestionIndex}
                onSelect={(v) => selectSuggestion('tags', v)}
              />
              <FilterField
                label="ISRC"
                icon={<Hash size={10} />}
                value={filters.isrc}
                onChange={(v) => {
                  setFilters((prev) => ({ ...prev, isrc: v }));
                  updateSuggestions('isrc', v);
                }}
                onFocus={() => handleFieldFocus('isrc')}
                onBlur={handleFieldBlur}
                onKeyDown={(e) => handleFieldKeyDown(e, 'isrc')}
                placeholder="ISRC code..."
                suggestions={activeSuggestionField === 'isrc' ? filteredSuggestions : []}
                suggestionIndex={suggestionIndex}
                onSelect={(v) => selectSuggestion('isrc', v)}
              />
              <FilterField
                label="Writer / Producer"
                icon={<Globe size={10} />}
                value={filters.isni}
                onChange={(v) => setFilters((prev) => ({ ...prev, isni: v }))}
                onFocus={() => {}}
                onBlur={() => {}}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') executeSearch();
                }}
                placeholder="Writer or producer..."
                suggestions={[]}
                suggestionIndex={-1}
                onSelect={() => {}}
              />

              <div className="space-y-1">
                <label className="flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider text-gray-500">
                  <Filter size={10} /> Status
                </label>
                <select
                  value={filters.status}
                  onChange={(e) => setFilters((prev) => ({ ...prev, status: e.target.value }))}
                  className="w-full bg-white/[0.03] border border-white/[0.08] rounded px-2.5 py-1.5 text-xs text-white outline-none focus:border-[#C4AB04]/40 transition appearance-none cursor-pointer"
                >
                  <option value="" className="bg-[#111]">Any status</option>
                  {STATUS_OPTIONS.map((s) => (
                    <option key={s} value={s} className="bg-[#111]">
                      {s.replace('_', ' ')}
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-1">
                <label className="flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider text-gray-500">
                  <Music2 size={10} /> Key
                </label>
                <select
                  value={filters.key_signature}
                  onChange={(e) => setFilters((prev) => ({ ...prev, key_signature: e.target.value }))}
                  className="w-full bg-white/[0.03] border border-white/[0.08] rounded px-2.5 py-1.5 text-xs text-white outline-none focus:border-[#C4AB04]/40 transition appearance-none cursor-pointer"
                >
                  <option value="" className="bg-[#111]">Any key</option>
                  {suggestions.key_signature.map((k) => (
                    <option key={k} value={k} className="bg-[#111]">
                      {k}
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-1">
                <label className="flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider text-gray-500">
                  <Zap size={10} /> BPM Range
                </label>
                <div className="flex items-center gap-1.5">
                  <input
                    type="number"
                    value={filters.bpm_min}
                    onChange={(e) => setFilters((prev) => ({ ...prev, bpm_min: e.target.value }))}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') executeSearch();
                    }}
                    placeholder="Min"
                    className="w-1/2 bg-white/[0.03] border border-white/[0.08] rounded px-2 py-1.5 text-xs text-white outline-none focus:border-[#C4AB04]/40 transition placeholder-gray-600"
                  />
                  <span className="text-gray-600 text-xs">—</span>
                  <input
                    type="number"
                    value={filters.bpm_max}
                    onChange={(e) => setFilters((prev) => ({ ...prev, bpm_max: e.target.value }))}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') executeSearch();
                    }}
                    placeholder="Max"
                    className="w-1/2 bg-white/[0.03] border border-white/[0.08] rounded px-2 py-1.5 text-xs text-white outline-none focus:border-[#C4AB04]/40 transition placeholder-gray-600"
                  />
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between mt-4 pt-3 border-t border-white/[0.04]">
              <button
                type="button"
                onClick={clearSearch}
                className="text-[10px] font-bold uppercase tracking-wider text-gray-500 hover:text-white transition"
              >
                Clear All
              </button>
              <button
                type="button"
                onClick={executeSearch}
                disabled={searching}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-[#C4AB04]/10 border border-[#C4AB04]/30 text-[#C4AB04] text-xs font-bold uppercase tracking-wider hover:bg-[#C4AB04]/20 transition disabled:opacity-50"
              >
                <Search size={12} />
                {searching ? 'Searching...' : 'Search'}
              </button>
            </div>
          </div>
        )}
      </div>

      {hasSearched && (
        <div className="mt-3 bg-black/90 rounded-lg border border-white/[0.06] shadow-[0_4px_4px_rgba(0,0,0,0.17)] overflow-hidden animate-[slideDown_200ms_ease-out]">
          <div className="px-4 py-2.5 border-b border-white/[0.06] flex items-center justify-between">
            <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400">
              {searching ? 'Searching...' : `${results.length} result${results.length !== 1 ? 's' : ''}`}
            </span>
            {results.length > 0 && (
              <span className="text-[9px] text-gray-500">Click to open</span>
            )}
          </div>

          {results.length === 0 && !searching ? (
            <div className="px-6 py-8 text-center">
              <Search size={24} className="mx-auto text-gray-600 mb-2" />
              <div className="text-xs text-gray-500">No matches found across the catalog</div>
            </div>
          ) : (
            <ul className="max-h-[320px] overflow-y-auto scrollbar-thin">
              {results.map((result, i) => (
                <li
                  key={`${result.type}-${result.id}`}
                  onClick={() => openResult(result)}
                  className="px-4 py-3 flex items-center gap-3 hover:bg-white/[0.03] cursor-pointer border-b border-white/[0.03] last:border-b-0 transition-colors"
                  style={{ animationDelay: `${i * 30}ms` }}
                >
                  <div className="w-8 h-8 rounded bg-white/[0.04] flex items-center justify-center shrink-0">
                    <Music2 size={14} className="text-[#C4AB04]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-white truncate">{result.title}</div>
                    <div className="text-[10px] text-gray-500 truncate">
                      {resultTypeLabel(result.type)}
                      {result.artist ? ` · ${result.artist}` : ''}
                      {result.album ? ` · ${result.album}` : ''}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {result.bpm && (
                      <span className="text-[9px] font-mono text-gray-500">{result.bpm} BPM</span>
                    )}
                    {result.song_key && (
                      <span className="text-[9px] font-mono text-gray-500">{result.song_key}</span>
                    )}
                    {result.status && <StatusPill status={result.status} />}
                    {result.duration && (
                      <span className="text-[10px] font-mono text-gray-500 w-10 text-right">
                        {result.duration}
                      </span>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default CatalogSearch;
